import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import { apiHost } from '../../api/apiClient';
import { retrieveRecording, retrievalStatus, retrievalWarning, retrievalError } from './videoRetrievalApi';
import VideoUsage from './VideoUsage';
import './video.css';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const localTime = date => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const message = error => error.response?.data?.message || (error.code === 'ECONNABORTED' ? 'The camera request took too long. Try again.' : null) || 'Video service is unavailable. No stream has been started.';
const mb = bytes => (Number(bytes || 0) / 1000000).toFixed(2);
const withRetrieval = (recording, job) => ({ ...recording,
  retrievalStatus: job.status, retrievalStatusKnown: true, retrievalJobId: job.id,
  retrievalAvailable: job.status === 'Ready' && job.canDownload === true });
const openRetrievedRecordings = event => {
  // Match Realtime Quick Reports: open through the originating browser window when available.
  if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  const href = new URL(event.currentTarget.getAttribute('href') || '/video-retrievals', window.location.origin).href;
  try {
    const browserWindow = window.opener && !window.opener.closed ? window.opener : window;
    const reportWindow = browserWindow.open(href, '_blank');
    if (reportWindow) { reportWindow.focus(); return; }
  } catch { /* A cross-origin opener cannot be used; open through this window instead. */ }
  window.open(href, '_blank');
};

export default function VideoPanel({ asset, onClose }) {
  const [retrievalBusy, setRetrievalBusy] = useState(false);
  const [requestingClipId, setRequestingClipId] = useState('');
  const [retrievalJob, setRetrievalJob] = useState(null);
  const [retrievalMessage, setRetrievalMessage] = useState('');
  const retrievalPending = useRef(false);
  useEffect(() => {
    if (!retrievalJob?.id || ['Ready', 'Failed', 'Deleted'].includes(retrievalJob.status)) return undefined;
    let alive = true;
    let polling = false;
    const timer = setInterval(async () => {
      if (polling || document.hidden) return;
      polling = true;
      try {
        const next = await retrievalStatus(retrievalJob.id);
        if (alive) {
          setRetrievalJob(next);
          setClips(previous => previous.map(recording => recording.retrievalJobId === next.id ? withRetrieval(recording, next) : recording));
        }
      }
      catch (error) { if (alive) setRetrievalMessage(retrievalError(error)); }
      finally { polling = false; }
    }, 10000);
    return () => { alive = false; clearInterval(timer); };
  }, [retrievalJob?.id, retrievalJob?.status]);
  const retrieve = async recording => {
    if (retrievalPending.current || !currentPanel.current || !window.confirm(`${retrievalWarning}\n\nEstimated size: ${recording.size || 'unknown'}\n\nRetrieve CH${recording.channel}, ${recording.start} to ${recording.end}?`)) return;
    retrievalPending.current = true; setRetrievalBusy(true); setRequestingClipId(recording.clipId); setRetrievalMessage('');
    try {
      const job = await retrieveRecording(currentPanel.current, recording.clipId);
      if (mounted.current) {
        setRetrievalJob(job);
        setClips(previous => previous.map(clip => clip.clipId === recording.clipId ? withRetrieval(clip, job) : clip));
      }
    }
    catch (error) { if (mounted.current) setRetrievalMessage(retrievalError(error)); }
    finally { retrievalPending.current = false; if (mounted.current) { setRetrievalBusy(false); setRequestingClipId(''); } }
  };
  const [panel, setPanel] = useState(null), [channels, setChannels] = useState([]), [selected, setSelected] = useState([1]);
  const [mode, setMode] = useState('Live'), [quality, setQuality] = useState('Sub'), [clips, setClips] = useState([]), [clip, setClip] = useState('');
  const [playbackChannel, setPlaybackChannel] = useState('all'), [searchComplete, setSearchComplete] = useState(false), [searchNote, setSearchNote] = useState('');
  const [resultChannel, setResultChannel] = useState('all');
  const searchCancel = useRef(null);
  const [start, setStart] = useState(localTime(new Date(Date.now() - 1800000))), [end, setEnd] = useState(localTime(new Date()));
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [status, setStatus] = useState('Connecting to video service…');
  const [playing, setPlaying] = useState(false), [ready, setReady] = useState(false), [usage, setUsage] = useState([]), [showUsage, setShowUsage] = useState(false);
  const [showViewingNotice, setShowViewingNotice] = useState(true);
  const [cameraConnection, setCameraConnection] = useState({ status: 'checking', checkedUtc: null });
  const [waitingSince, setWaitingSince] = useState(null), [waitingSeconds, setWaitingSeconds] = useState(0);
  const currentStreams = useRef([]), playbackMode = useRef(false), clipRef = useRef('');
  const [receivingData, setReceivingData] = useState(false);
  const lastReceived = useRef(0), seekHandler = useRef(null), seeking = useRef(false), recoveryUsed = useRef(false);
  const frame = useRef(null), currentPanel = useRef(null), mounted = useRef(true), operation = useRef(0), active = useRef(false), actionBusy = useRef(false);
  const send = useCallback(data => frame.current?.contentWindow?.postMessage({ source: 'visiontrak-video', ...data }, window.location.origin), []);
  const post = useCallback(async (suffix, data = {}, keepalive = false) => {
    const controller = new AbortController();
    const timeout = keepalive ? null : setTimeout(() => controller.abort(), 12000);
    try { return await fetch(`${apiHost}/api/Video/Panels/${suffix}`, {
      method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, body: JSON.stringify(data), keepalive, signal: controller.signal,
    }); } finally { clearTimeout(timeout); }
  }, []);
  const stop = useCallback(async () => {
    operation.current++; active.current = false; send({ type: 'stop' });
    if (mounted.current) { setPlaying(false); setWaitingSince(null); setStatus('Stopped'); }
    if (currentPanel.current) {
      try { const response = await post(`${currentPanel.current}/Stop`); if (!response.ok) throw new Error('Stop rejected'); }
      catch { if (mounted.current) setError('Stop could not be confirmed. The server heartbeat timeout will stop remaining streams.'); return false; }
    }
    return true;
  }, [post, send]);
  useEffect(() => {
    setShowViewingNotice(true);
    const timer = setTimeout(() => setShowViewingNotice(false), 20000);
    return () => clearTimeout(timer);
  }, [asset.assetId]);
  useEffect(() => {
    let cancelled = false, loading = false, request, staleTimer;
    const refresh = async () => {
      if (cancelled || loading || document.hidden) return;
      loading = true; request = axios.CancelToken.source();
      try {
        const response = await axios.post(`${apiHost}/api/Video/CameraStatus`, [asset.assetId], { headers: headers(), timeout: 30000, cancelToken: request.token });
        if (cancelled || document.hidden) return;
        const row = Array.isArray(response.data) ? response.data.find(r => String(r.assetId) === String(asset.assetId)) : null;
        const status = ['online', 'offline'].includes(row?.status) ? row.status : 'unknown';
        setCameraConnection({ status, checkedUtc: row?.checkedUtc || null });
        clearTimeout(staleTimer);
        staleTimer = setTimeout(() => { if (!cancelled) setCameraConnection({ status: 'unknown', checkedUtc: null }); }, 45000);
      } catch (e) {
        if (!cancelled && !axios.isCancel(e)) setCameraConnection({ status: 'unknown', checkedUtc: null });
      } finally { loading = false; }
    };
    const visibility = () => {
      if (document.hidden) {
        request?.cancel(); clearTimeout(staleTimer);
        setCameraConnection({ status: 'unknown', checkedUtc: null });
      } else refresh();
    };
    setCameraConnection({ status: 'checking', checkedUtc: null }); refresh();
    const timer = setInterval(refresh, 30000);
    document.addEventListener('visibilitychange', visibility);
    return () => { cancelled = true; clearInterval(timer); clearTimeout(staleTimer); request?.cancel(); document.removeEventListener('visibilitychange', visibility); };
  }, [asset.assetId]);
  useEffect(() => {
    mounted.current = true;
    let cancelled = false, interval;
    const close = () => {
      operation.current++; active.current = false; send({ type: 'stop' });
      const id = currentPanel.current; currentPanel.current = null;
      if (id) post(`${id}/Close`, {}, true).catch(() => {});
    };
    const heartbeat = async () => {
      const id = currentPanel.current, version = operation.current;
      if (!id) return;
      try {
        const response = await post(`${id}/Heartbeat`);
        if (!response.ok) throw new Error('expired');
        const data = await response.json();
        if (!cancelled) {
          const recorded = (data.streams || []).map(row => Object.fromEntries(Object.entries(row).map(([key, value]) => [key[0].toUpperCase() + key.slice(1), value])));
          setUsage(recorded);
          const latest = recorded.filter(s => currentStreams.current.includes(s.Id));
          if (active.current && !actionBusy.current && operation.current === version && latest.length && latest.every(s => s.EndedUtc)) {
            if (playbackMode.current && latest.every(s => ['Completed', 'DataLimit', 'TimeLimit'].includes(s.Status))) {
              send({ type: 'transfer-ended' });
              if (latest.some(s => s.Status !== 'Completed')) setStatus('Transfer limit reached — playing received video');
            } else {
              active.current = false; send({ type: 'stop' }); setPlaying(false); setWaitingSince(null);
              setStatus(latest.some(s => s.Status === 'CameraDataTimeout') ? 'The camera did not send data within 20 seconds. Try Play again.' : 'Stream finished or reached its limit');
            }
          }
        }
      } catch {
        if (!cancelled) { close(); setPanel(null); setChannels([]); setPlaying(false); setWaitingSince(null); setError('Video session expired or lost its connection. Close and reopen Video.'); }
      }
    };
    (async () => {
      try {
        const opened = await axios.post(`${apiHost}/api/Video/Panels`, { assetId: asset.assetId }, { headers: headers() });
        if (cancelled) { post(`${opened.data.panelId}/Close`, {}, true).catch(() => {}); return; }
        currentPanel.current = opened.data.panelId; setPanel(opened.data);
        interval = setInterval(heartbeat, 10000);
        const info = await axios.get(`${apiHost}/api/Video/Panels/${opened.data.panelId}/Camera`, { headers: headers() });
        if (!cancelled) { setChannels(Array.from({ length: info.data.channels }, (_, i) => i + 1)); setStatus('Ready — video uses mobile data'); }
      } catch (e) { if (!cancelled) { setError(message(e)); setStatus('Unavailable'); } }
    })();
    const pageHidden = () => { cancelled = true; clearInterval(interval); close(); setPanel(null); setPlaying(false); setWaitingSince(null); setStatus('Session closed — close and reopen Video'); };
    window.addEventListener('pagehide', pageHidden);
    return () => { searchCancel.current?.cancel(); cancelled = true; mounted.current = false; clearInterval(interval); window.removeEventListener('pagehide', pageHidden); close(); };
  }, [asset.assetId, post, send]);
  useEffect(() => {
    const receive = event => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow || event.data?.source !== 'visiontrak-video') return;
      if (event.data.type === 'seek') { seekHandler.current?.(event.data.offsetSeconds); return; }
      if (event.data.type === 'decoder-reset') { seekHandler.current?.(event.data.offsetSeconds, true); return; }
      if (event.data.type === 'buffered-seek') {
        recoveryUsed.current = false;
        const version = operation.current;
        post(`${currentPanel.current}/BufferedSeek`, { clipId: clipRef.current, positionSeconds: event.data.offsetSeconds, streamId: event.data.streamId })
          .then(response => { if (!response.ok) throw new Error('Audit rejected'); })
          .catch(() => { if (mounted.current && operation.current === version) { stop(); setError('The playback seek could not be logged. Please try again.'); } });
        return;
      }
      if (event.data.type === 'ready') setReady(true);
      if (event.data.type === 'connected') setStatus('Connected — buffering picture…');
      if (event.data.type === 'playing') { setWaitingSince(null); setStatus('Playing'); }
      if (['failed', 'ended'].includes(event.data.type) && active.current) {
        stop(); setStatus(event.data.type === 'ended' ? 'Playback finished' : 'Connection ended — press Play to reconnect');
        if (event.data.reason) setError(event.data.reason);
      }
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [stop, post]);
  useEffect(() => {
    const hidden = () => { if (document.hidden && active.current) { stop(); setStatus('Stopped while the video tab was hidden'); } };
    document.addEventListener('visibilitychange', hidden);
    return () => document.removeEventListener('visibilitychange', hidden);
  }, [stop]);
  const changeMode = async next => {
    if (actionBusy.current) return;
    actionBusy.current = true; setBusy(true); await stop();
    try {
      if (currentPanel.current) await axios.post(`${apiHost}/api/Video/Panels/${currentPanel.current}/Mode`, { mode: next }, { headers: headers() });
      if (mounted.current) { setMode(next); if (next === 'Live') setQuality('Sub'); setClips([]); setClip(''); setSearchComplete(false); setSearchNote(''); setError(''); }
    } catch (e) { if (mounted.current) setError(message(e)); }
    finally { actionBusy.current = false; if (mounted.current) setBusy(false); }
  };
  const search = async () => {
    if (actionBusy.current || !currentPanel.current) return;
    if (!start || !end || !Number.isFinite(new Date(start).getTime()) || !Number.isFinite(new Date(end).getTime()) || new Date(start) >= new Date(end)) {
      setError('Select valid recording times with From earlier than To.'); return;
    }
    actionBusy.current = true; setBusy(true); setError(''); setClips([]); setClip(''); setSearchComplete(false); setSearchNote(''); setResultChannel('all');
    const cancel = axios.CancelToken.source(); searchCancel.current = cancel;
    const wanted = playbackChannel === 'all' ? channels : [Number(playbackChannel)];
    const found = [], failed = [], capped = [], empty = [];
    try {
      for (let index = 0; index < wanted.length && mounted.current; index++) {
        const channel = wanted[index];
        setStatus(`Searching CH${channel} (${index + 1} of ${wanted.length})…`);
        try {
          const response = await axios.post(`${apiHost}/api/Video/Panels/${currentPanel.current}/Recordings`, { channel, start, end }, { headers: headers(), timeout: 75000, cancelToken: cancel.token });
          if (!Array.isArray(response.data)) throw new Error('Invalid recording response');
          if (!response.data.length) empty.push(channel);
          if (response.data.length >= 100) capped.push(channel);
          found.push(...response.data);
        } catch (e) {
          if (axios.isCancel(e)) break;
          failed.push({ channel, message: e.response?.data?.message || (e.code === 'ECONNABORTED' ? 'Search timed out.' : 'Search could not complete.') });
        }
      }
      if (mounted.current) {
        found.sort((a, b) => a.start.localeCompare(b.start) || a.channel - b.channel);
        setClips(found); setSearchComplete(true);
        const notes = [];
        if (capped.length) notes.push(`CH${capped.join(', CH')}: 100 or more recordings returned. If footage is missing, narrow the time range.`);
        if (empty.length) notes.push(`CH${empty.join(', CH')}: the camera server returned no recordings. If footage is expected, the camera may not have answered in time; search again or choose a smaller range.`);
        failed.forEach(failure => notes.push(`CH${failure.channel}: ${failure.message} Results may be incomplete.`));
        setSearchNote(notes.join(' '));
        setStatus(found.length ? `${found.length} recordings found` : failed.length ? 'Recording search incomplete' : 'No recordings found for this time range');
      }
    } finally {
      if (searchCancel.current === cancel) searchCancel.current = null;
      actionBusy.current = false; if (mounted.current) setBusy(false);
    }
  };
  const begin = async (recording, positionSeconds, recovery = false) => {
    if (actionBusy.current || !currentPanel.current || !ready) return;
    const chosenClip = mode === 'Playback' ? recording || clips.find(c => c.clipId === clip) : null;
    if (mode === 'Playback' && !chosenClip) return;
    if (chosenClip) { clipRef.current = chosenClip.clipId; setClip(chosenClip.clipId); setQuality(chosenClip.quality); }
    if (!recovery) recoveryUsed.current = false;
    actionBusy.current = true; setBusy(true); setError('');
    setWaitingSince(Date.now()); setStatus(positionSeconds === undefined ? 'Requesting video…' : 'Requesting recording position…');
    const id = currentPanel.current, version = ++operation.current;
    try {
      const wanted = mode === 'Live' ? selected : [chosenClip.channel];
      const streams = [];
      // Sequential creation prevents a partially failed batch from leaving untracked streams.
      for (const channel of wanted) {
        const response = await axios.post(`${apiHost}/api/Video/Panels/${id}/Streams`, { mode, quality: chosenClip ? chosenClip.quality : quality, channel, clipId: chosenClip ? chosenClip.clipId : undefined, positionSeconds: chosenClip ? positionSeconds : undefined }, { headers: headers(), timeout: 15000 });
        if (!mounted.current || operation.current !== version) { await post(`${id}/Stop`); return; }
        streams.push(response.data);
      }
      if (document.hidden) { await stop(); setStatus('Stopped while the video tab was hidden'); return; }
      currentStreams.current = streams.map(s => s.streamId); playbackMode.current = mode === 'Playback';
      active.current = true; setPlaying(true); setStatus('Connecting…');
      send({ type: 'start', streams, apiOrigin: apiHost });
      if (chosenClip) frame.current?.scrollIntoView({ block: 'center' });
    } catch (e) { await stop(); if (mounted.current) setError(message(e)); }
    finally { actionBusy.current = false; if (mounted.current) setBusy(false); }
  };
  seekHandler.current = async (offsetSeconds, recovery = false) => {
    if (mode !== 'Playback' || !active.current || actionBusy.current || seeking.current || !Number.isInteger(offsetSeconds) || offsetSeconds < 0) return;
    const recording = clips.find(c => c.clipId === clip);
    if (!recording) return;
    if (recovery && recoveryUsed.current) {
      await stop();
      if (mounted.current) setError('The decoder could not play this position after reconnecting. Try a nearby position or another recording.');
      return;
    }
    if (recovery) recoveryUsed.current = true;
    seeking.current = true;
    const expectedVersion = operation.current + 1;
    try {
      if (!await stop()) return;
      if (!mounted.current || operation.current !== expectedVersion) return;
      setStatus('Seeking to recording position…');
      await begin(recording, offsetSeconds, recovery);
    } finally { seeking.current = false; }
  };
  useEffect(() => {
    setWaitingSeconds(0);
    if (!waitingSince) return;
    const timer = setInterval(() => setWaitingSeconds(Math.floor((Date.now() - waitingSince) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [waitingSince]);
  const filteredClips = resultChannel === 'all' ? clips : clips.filter(recording => recording.channel === Number(resultChannel));
  const currentRecording = clips.find(recording => recording.clipId === clip);
  const received = usage.reduce((sum, s) => sum + Number(s.ReceivedBytes || 0), 0);
  useEffect(() => {
    const increased = received > lastReceived.current;
    lastReceived.current = received;
    setReceivingData(playing && mode === 'Live' && increased);
    if (playing && mode === 'Live' && increased) {
      const timer = setTimeout(() => setReceivingData(false), 1600);
      return () => clearTimeout(timer);
    }
  }, [received, playing, mode]);
  return <Modal show onHide={onClose} size="xl" className="video-panel-modal" backdrop="static">
    <Modal.Header closeButton><Modal.Title className="video-modal-title"><span>Video — {asset.assetName}</span>
      <span className={`video-camera-status${cameraConnection.status === 'online' ? ' video-camera-status-online' : ''}`} role="status"
        title={`Camera connection refreshes every 30 seconds${cameraConnection.checkedUtc ? ` · Checked ${new Date(cameraConnection.checkedUtc).toLocaleTimeString()}` : ''}`}>
        <span className="video-camera-status-dot" aria-hidden="true" />
        {cameraConnection.status === 'online' ? 'Camera online' : cameraConnection.status === 'offline' ? 'Camera offline' : cameraConnection.status === 'checking' ? 'Checking camera…' : 'Camera status unavailable'}
      </span></Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className="video-tabs" role="tablist" aria-label="Video mode">
        {['Live', 'Playback'].map(value => <button key={value} role="tab" aria-selected={mode === value} className={mode === value ? 'active' : ''} disabled={busy} onClick={() => changeMode(value)}>{value}</button>)}
        {mode === 'Playback' && <span className="video-playback-description">Recordings on the device. Enter times in the camera’s local timezone; searching can use a small amount of SIM data.</span>}
        <button className="video-usage-link" onClick={() => setShowUsage(true)}>My video usage</button>
      </div>
      {error && <div className="alert alert-warning" role="alert">{error}</div>}
      <div className="video-controls">
        {mode === 'Live' && <fieldset disabled={playing || busy}><legend>Channels {mode === 'Live' && '(up to four)'}</legend>
          {channels.map(channel => <label key={channel}><input type={mode === 'Live' ? 'checkbox' : 'radio'} name="video-channel" checked={mode === 'Live' ? selected.includes(channel) : selected[0] === channel} onChange={() => {
            setClips([]); setClip('');
            if (mode === 'Playback') setSelected([channel]);
            else setSelected(previous => previous.includes(channel) ? previous.filter(c => c !== channel) : previous.length < 4 ? [...previous, channel] : previous);
          }} /> CH{channel}</label>)}
        </fieldset>}
        {mode === 'Live' && <label>Quality <select aria-describedby={mode === 'Live' && quality === 'Main' ? 'video-main-stream-notice' : undefined} value={quality} disabled={playing || busy || mode === 'Playback'} onChange={e => setQuality(e.target.value)}><option value="Sub">Sub stream — lower data</option><option value="Main">Main stream — higher data</option></select></label>}
      </div>
      {mode === 'Live' && quality === 'Main' && <div id="video-main-stream-notice" className="video-main-stream-notice" role="status">
        <i className="fa fa-info-circle" aria-hidden="true"></i>
        <div><strong>Main stream uses more mobile data</strong><p>For lower-data viewing, choose Sub stream. Viewing more channels also increases data use.</p></div>
      </div>}
      {mode === 'Playback' && <div className="video-playback-controls">
        <label>Search channels <select value={playbackChannel} disabled={playing || busy} onChange={e => { setPlaybackChannel(e.target.value); setClips([]); setClip(''); setSearchComplete(false); setSearchNote(''); }}><option value="all">All channels</option>{channels.map(channel => <option key={channel} value={channel}>CH{channel}</option>)}</select></label>
        <label>From <input type="datetime-local" value={start} disabled={playing || busy} onChange={e => { setStart(e.target.value); setClips([]); setClip(''); setSearchComplete(false); setSearchNote(''); }} /></label>
        <label>To <input type="datetime-local" value={end} disabled={playing || busy} onChange={e => { setEnd(e.target.value); setClips([]); setClip(''); setSearchComplete(false); setSearchNote(''); }} /></label>
        <button onClick={search} disabled={!panel || !channels.length || busy || playing || !start || !end}>Search recordings</button>

      </div>}
      {showViewingNotice && <div id="video-viewing-notice" className="video-viewing-notice" role="note">
        <i className="fa fa-info-circle" aria-hidden="true"></i>
        <div><strong>Viewing is logged for SIM data usage reporting</strong>
          <p>Live viewing and playback use the asset’s mobile data. We log the viewer, time, duration and measured video data separately from everyday tracking to support usage reporting.</p>
          <small>Each transfer stops after five minutes or 50 MB per channel. Received footage can keep playing. You can restart when needed.</small>
        </div>
      </div>}
      <div className="video-actions">
        {mode === 'Live' && <button className="btn btn-primary" aria-describedby={showViewingNotice ? "video-viewing-notice" : undefined} onClick={() => begin()} disabled={!panel || !channels.length || !ready || busy || playing || !selected.length}>Start live</button>}
        <button className="btn btn-outline-danger" onClick={stop} disabled={!playing && !busy}><i className="fa fa-stop" aria-hidden="true"></i> Stop video</button>
        <button className="btn btn-outline-secondary" onClick={() => frame.current?.requestFullscreen?.()}>Fullscreen</button>
        <span role="status">{status}{waitingSince && waitingSeconds >= 5 ? ` (${waitingSeconds}s — waiting for the camera/picture)` : ''} · Gateway received: <span key={received} className={`video-data-value${receivingData ? " video-data-value-receiving" : ""}`} title="Measured gateway bytes update approximately every 10 seconds">{mb(received)} MB</span></span>
      </div>
      {mode === 'Playback' && playing && <small className="video-seek-hint">Drag to any point in the recording. Grey sections seek immediately; other positions reconnect automatically.</small>}
      <div className="video-player-frame"><iframe ref={frame} src="/video-sdk/player.html?v=quiet5-20261001" title="Asset video player" allow="fullscreen" sandbox="allow-scripts allow-same-origin" /></div>
      {mode === 'Playback' && playing && <div className="video-playback-stop-bar" role="status">
        <span>Playing {currentRecording ? `CH${currentRecording.channel} — ${currentRecording.start}` : 'recording'}</span>
        <button type="button" className="btn btn-outline-danger" onClick={stop}><i className="fa fa-stop" aria-hidden="true"></i> Stop playback</button>
      </div>}
      <small>Received bytes measure video reaching our server, including buffered data. They are not carrier-confirmed SIM usage. Multiple channels are counted separately.</small>
      {mode === 'Playback' && <section className="video-recordings" aria-label="Recording search results">
        <div className="video-retrieval-status" role="status">
          <a href="/video-retrievals" target="_blank" rel="noopener noreferrer" onClick={openRetrievedRecordings}>Retrieved recordings</a>
          {retrievalJob && <span> — CH{retrievalJob.channel}: {retrievalJob.status}{retrievalJob.progressPercent != null ? ` (${retrievalJob.progressPercent}%)` : ''}. Retrieval continues after this panel closes.</span>}
          {retrievalMessage && <p role="alert">{retrievalMessage}</p>}
        </div>
        <div className="video-recordings-toolbar"><h5>Recordings {searchComplete && `(${filteredClips.length} of ${clips.length})`}</h5>
          {clips.length > 0 && <label>Filter channel <select value={resultChannel} onChange={e => setResultChannel(e.target.value)}><option value="all">All channels</option>{channels.map(channel => <option key={channel} value={channel}>CH{channel}</option>)}</select></label>}
        </div>
        {clips.length > 0 && <p id="video-playback-result-notice" className="video-recording-notice">Playback uses mobile data and is logged for SIM usage reporting.</p>}
        {searchNote && <div className="alert alert-warning" role="status">{searchNote}</div>}
        {!clips.length && <p role="status">{busy ? 'Searching recordings…' : searchComplete ? 'No recordings returned. Check the search status above or try another time range.' : 'Search a time range to find recordings across all channels or a selected channel.'}</p>}
        <div className="video-recording-list" role="region" aria-label="Playback recordings" tabIndex={0}>
          <table className="video-recording-table"><thead><tr><th>Channel</th><th>Start</th><th>End</th><th>Duration</th><th>Quality</th><th>File size</th><th>Playback</th><th>Retrieve</th></tr></thead><tbody>
            {filteredClips.map(recording => <tr className={[clip === recording.clipId && playing ? 'active' : '', recording.retrievalAvailable ? 'video-recording-available' : '', ['Requested', 'Uploading', 'Processing'].includes(recording.retrievalStatus) ? 'video-recording-pending' : '', requestingClipId === recording.clipId ? 'video-recording-requesting' : ''].filter(Boolean).join(' ')} key={recording.clipId}>
              <td>CH{recording.channel}</td><td>{recording.start}</td><td>{recording.end}</td>
              <td>{Math.floor(Math.max(0, (new Date(recording.end.replace(' ', 'T') + 'Z') - new Date(recording.start.replace(' ', 'T') + 'Z')) / 1000) / 60)}m {Math.max(0, Math.round((new Date(recording.end.replace(' ', 'T') + 'Z') - new Date(recording.start.replace(' ', 'T') + 'Z')) / 1000)) % 60}s</td>
              <td>{recording.quality}</td><td>{recording.size || 'Not reported'}</td>
              <td>{clip === recording.clipId && playing ? <button type="button" className="btn btn-outline-danger btn-sm" aria-label="Stop playback" onClick={stop}><i className="fa fa-stop" aria-hidden="true"></i> Stop</button> : <button type="button" className="btn btn-primary btn-sm" aria-describedby="video-viewing-notice video-playback-result-notice" aria-label={`Play recording on channel ${recording.channel} starting ${recording.start}`} disabled={!panel || !ready || busy || playing} onClick={() => begin(recording)}><i className="fa fa-play" aria-hidden="true"></i> Play</button>}</td>
              <td>{recording.retrievalAvailable ?
                <a href={recording.retrievalJobId ? `/video-retrievals?recording=${encodeURIComponent(recording.retrievalJobId)}` : "/video-retrievals"} target="_blank" rel="noopener noreferrer" className="btn btn-outline-success btn-sm" title="Already uploaded. Open Retrieved recordings to download the server copy." onClick={openRetrievedRecordings}>Available</a> :
                recording.retrievalStatus || recording.retrievalStatusKnown === false ?
                  <a href={recording.retrievalJobId ? `/video-retrievals?recording=${encodeURIComponent(recording.retrievalJobId)}` : "/video-retrievals"} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm" title="Open Retrieved recordings to check the existing retrieval status." onClick={openRetrievedRecordings}>{['Requested', 'Uploading', 'Processing'].includes(recording.retrievalStatus) ? 'In progress' : 'Check retrieval'}</a> :
                  <button type="button" className="btn btn-outline-primary btn-sm" disabled={!recording.retrievalEnabled || !panel || retrievalBusy} title={!recording.retrievalEnabled ? 'Retrieval awaits environment validation' : 'Retrieve to video server'} onClick={() => retrieve(recording)}>{requestingClipId === recording.clipId ? 'Requesting…' : 'Retrieve'}</button>}</td>
            </tr>)}
            {clips.length > 0 && !filteredClips.length && <tr><td colSpan={8}>No recordings for this channel in the current search results.</td></tr>}
          </tbody></table>
        </div>
      </section>}
      {showUsage && <VideoUsage own onClose={() => setShowUsage(false)} />}
    </Modal.Body>
  </Modal>;
}
